import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/Login.css';
import config from '../config';
import { saveDeviceSettings, getDeviceSettings } from '../utils/device';
import { checkPasskeySupport, registerPasskey, loginWithPasskey } from '../utils/passkey';

interface LoginProps {
  onLoginSuccess: (user: any, token: string) => void;
}

interface PlayerMatch {
  id: string;
  name: string;
  club: string;
  level: string;
}

// ─── Fase type voor de poortwachter flow ───────────────────────────────────────
type GatekeeperPhase =
  | 'email_input'       // Fase 0: Toon alleen e-mail + "Doorgaan"
  | 'passkey_prompt'    // Fase A: Bestaande gebruiker, passkey gevonden
  | 'register_expand'   // Fase B: Nieuwe gebruiker, formulier uitklappen
  | 'otp_verify'        // Verificatiecode invoeren
  | 'passkey_register'  // Eerste passkey registratie aanbieden
  | 'player_select';    // Meerdere SBN-profielen gevonden

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  // ─── Email-first gatekeeper state ───────────────────────────────────────────
  const [phase, setPhase] = useState<GatekeeperPhase>(() => {
    // Start altijd in email_input tenzij een device+passkey bekend is (showPasskeyHero logica)
    const savedSettings = getDeviceSettings();
    if (savedSettings?.email && savedSettings?.passkeyCredentialId) {
      return 'passkey_prompt'; // Al bekend apparaat → direct Passkey Hero
    }
    return 'email_input';
  });

  // Prefill e-mail uit localStorage remembered_email
  const rememberedEmail = localStorage.getItem('remembered_email') || '';
  const savedSettings = getDeviceSettings();

  const [emailInput, setEmailInput] = useState(rememberedEmail);
  const [checkEmailLoading, setCheckEmailLoading] = useState(false);
  const [checkEmailError, setCheckEmailError] = useState('');

  // Gevonden gebruikersinfo na check-email
  const [knownUser, setKnownUser] = useState<any>(null);
  // AuthOptions van de poortwachter (WebAuthn challenge al aangemaakt in Redis)
  const [cachedAuthOptions, setCachedAuthOptions] = useState<any>(null);

  // ─── Registratie formulier state ─────────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: '',
    email: emailInput,
    mobile: ''
  });

  const [matches, setMatches] = useState<PlayerMatch[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerMatch | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [pollingActive, setPollingActive] = useState(false);
  const [whatsAppOpened, setWhatsAppOpened] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [passkeyRegistering, setPasskeyRegistering] = useState(false);
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);
  const [verifiedUser, setVerifiedUser] = useState<any>(null);
  const [passkeyLoginError, setPasskeyLoginError] = useState('');
  // Wordt true als gebruiker expliciet voor OTP kiest (passkey omzeilen, bijv. als credential verdwenen is)
  const [forceOtp, setForceOtp] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  const WHATSAPP_BOT_NUMBER = '31685887084';

  // Afgeleide waarde voor het volledige mobiele nummer
  let fullMobile = formData.mobile.trim();
  if (fullMobile.startsWith('06')) {
    fullMobile = '+31' + fullMobile.substring(1);
  } else if (fullMobile.startsWith('6') && fullMobile.length === 9) {
    fullMobile = '+31' + fullMobile;
  } else if (!fullMobile.startsWith('+') && fullMobile.length >= 10) {
    fullMobile = '+' + fullMobile;
  }

  const isRegisterFormComplete =
    formData.name.trim().length > 2 &&
    formData.email.includes('@') &&
    formData.mobile.trim().length >= 9;

  // ─── Passkey hardware support check bij laden ───────────────────────────────
  useEffect(() => {
    checkPasskeySupport().then(setPasskeySupported);
  }, []);

  // ─── Auto passkey-login bij "Welkom terug" fase ─────────────────────────────
  useEffect(() => {
    if (phase !== 'passkey_prompt') return;
    if (!passkeySupported) return;
    const settings = getDeviceSettings();
    if (!settings?.email || !settings?.passkeyCredentialId) return;

    const timer = setTimeout(() => {
      handlePasskeyLogin(settings.email!);
    }, 400);
    return () => clearTimeout(timer);
  }, [passkeySupported, phase]);

  // ─── OTP polling (verificatiestatus pollen) ─────────────────────────────────
  const checkVerificationStatus = useCallback(async () => {
    try {
      const email = formData.email || emailInput;
      const res = await fetch(
        `${config.apiUrl}/api/v1/auth/status?email=${encodeURIComponent(email)}&mobile=${encodeURIComponent(fullMobile)}`
      );
      const data = await res.json();

      if (data.success && data.verified && data.token) {
        setPollingActive(false);
        setWhatsAppOpened(false);
        try {
          await saveDeviceSettings(email, formData.name || knownUser?.name || '', selectedPlayer?.id || '');
        } catch (e) { /* niet fataal */ }
        handleVerificationComplete(data.token, data.user);
        return true;
      } else if (data.error === 'invalid_code') {
        setVerifyError('Controleer de code en probeer nogmaals');
      }
    } catch (err: any) {
      console.error('Status check error:', err);
    }
    return false;
  }, [formData.email, emailInput, fullMobile, formData.name, knownUser, selectedPlayer]);

  useEffect(() => {
    let interval: any;
    if (pollingActive && phase === 'otp_verify') {
      interval = setInterval(checkVerificationStatus, 3000);
    }
    return () => clearInterval(interval);
  }, [pollingActive, phase, checkVerificationStatus]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && pollingActive && phase === 'otp_verify') {
        checkVerificationStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [pollingActive, phase, checkVerificationStatus]);

  useEffect(() => {
    if (!pollingActive || phase !== 'otp_verify') return;
    try {
      const emailForWs = formData.email || emailInput;
      const ws = new WebSocket(config.wsUrl);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'AUTH_SUCCESS' && data.email === emailForWs) {
            checkVerificationStatus();
          }
        } catch (e) { /* parse error — negeer */ }
      };
      return () => {
        wsRef.current?.close();
        wsRef.current = null;
      };
    } catch (err) { /* WebSocket niet beschikbaar */ }
  }, [pollingActive, phase, formData.email, emailInput, checkVerificationStatus]);

  // ─── handleVerificationComplete (na OTP/passkey succes) ─────────────────────
  const handleVerificationComplete = useCallback(async (token: string, user: any) => {
    const isSupported = await checkPasskeySupport();
    setPasskeySupported(isSupported);
    setVerifiedToken(token);
    setVerifiedUser(user);

    const email = user?.email || formData.email || emailInput;

    try {
      await saveDeviceSettings(email, formData.name || user?.name || '', selectedPlayer?.id || '');
    } catch (e) { /* niet fataal */ }

    const savedSettingsRaw = localStorage.getItem('squash_device_settings');
    let alreadyHasPasskey = false;
    if (savedSettingsRaw) {
      try {
        const saved = JSON.parse(savedSettingsRaw);
        alreadyHasPasskey = saved.email?.toLowerCase() === email.toLowerCase() && !!saved.passkeyCredentialId;
      } catch (e) { /* parse fout */ }
    }

    if (!isSupported || alreadyHasPasskey) {
      onLoginSuccess(user, token);
    } else {
      setPhase('passkey_register');
    }
  }, [formData.email, formData.name, emailInput, selectedPlayer, onLoginSuccess]);

  // ─── FASE 0: E-mail Eerst — Poortwachter check ──────────────────────────────
  const handleCheckEmail = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email.includes('@') || email.length < 5) {
      setCheckEmailError('Voer een geldig e-mailadres in.');
      return;
    }
    setCheckEmailLoading(true);
    setCheckEmailError('');

    try {
      const res = await fetch(`${config.apiUrl}/api/v1/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();

      if (!data.success) {
        setCheckEmailError(data.message || 'Er is een fout opgetreden. Probeer opnieuw.');
        return;
      }

      if (data.exists) {
        // Scenario A: Bestaande gebruiker
        setKnownUser(data.user);
        localStorage.setItem('remembered_email', email);

        if (data.hasPasskey && data.authOptions && passkeySupported && !forceOtp) {
          // Sla challenge op en start direct biometrie
          setCachedAuthOptions(data.authOptions);
          setPasskeyLoginError('');
          await triggerPasskeyLogin(email, data.authOptions);
        } else {
          // Geen passkey, hardware-support ontbreekt, of gebruiker koos expliciet voor OTP → OTP sturen
          await sendOtpCode(email, data.user?.name || '', null);
        }
      } else {
        // Scenario B: Nieuwe gebruiker → uitklappen registratieformulier
        setFormData(prev => ({ ...prev, email }));
        setPhase('register_expand');
      }
    } catch (err: any) {
      setCheckEmailError('Verbindingsfout. Controleer of de server actief is.');
    } finally {
      setCheckEmailLoading(false);
    }
  };

  // ─── Passkey login triggeren (vanuit check-email of knop) ───────────────────
  const triggerPasskeyLogin = async (email: string, authOptions?: any) => {
    setLoading(true);
    setPasskeyLoginError('');
    try {
      const deviceSettings = getDeviceSettings();
      const result = await loginWithPasskey(email, deviceSettings.deviceId, authOptions);

      if (result && result.success) {
        const currentSettings = getDeviceSettings();
        localStorage.setItem('squash_device_settings', JSON.stringify({
          ...currentSettings,
          email,
          refereeName: result.user.name,
          refereePlayerId: result.user.playerId,
          passkeyCredentialId: result.credentialId,
          passkeyRegisteredAt: new Date().toISOString()
        }));
        localStorage.setItem('remembered_email', email);
        onLoginSuccess(result.user, result.token);
      } else if (result && !result.success) {
        if (result.cancelled) {
          // Gebruiker annuleerde biometrie → bied OTP fallback
          setPasskeyLoginError('Passkey geannuleerd. Inloggen met code?');
        } else {
          setPasskeyLoginError(result.error || 'Passkey login mislukt.');
        }
      } else {
        setPasskeyLoginError('Passkey login mislukt.');
      }
    } catch (err) {
      setPasskeyLoginError('Fout bij passkey login.');
    } finally {
      setLoading(false);
    }
  };

  // handlePasskeyLogin blijft compatibel voor het Passkey Hero scherm
  const handlePasskeyLogin = async (targetEmail?: string) => {
    const emailToUse = (targetEmail || emailInput).toLowerCase().trim();
    await triggerPasskeyLogin(emailToUse);
  };

  // ─── OTP code sturen ─────────────────────────────────────────────────────────
  const sendOtpCode = async (email: string, name: string, player: PlayerMatch | null) => {
    setLoading(true);
    setStatusMessage('Verificatiecode aanvragen...');
    setError('');
    try {
      const response = await fetch(`${config.apiUrl}/api/v1/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          mobile: fullMobile,
          name,
          playerId: player?.id,
          playerDetails: player
        }),
      });
      const data = await response.json();
      if (data.success) {
        setStatusMessage('Code verzonden!');
        setPhase('otp_verify');
        setPollingActive(true);
      } else {
        setError(data.message || 'Kon verificatiecode niet verzenden.');
      }
    } catch (err: any) {
      setError('Verbinding met server mislukt.');
    } finally {
      setLoading(false);
      setStatusMessage('');
    }
  };

  // ─── OTP code verificatie ────────────────────────────────────────────────────
  const handleVerifyDirect = async (codeToVerify?: string) => {
    const codeVal = (codeToVerify || code).trim();
    if (codeVal.length !== 6) {
      setVerifyError('Voer een geldige 6-cijferige code in');
      return;
    }
    setLoading(true);
    setVerifyError('');
    const email = formData.email || emailInput;
    try {
      const res = await fetch(`${config.apiUrl}/api/v1/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: codeVal }),
      });
      const data = await res.json();
      if (data.success && data.verified && data.token) {
        setPollingActive(false);
        setWhatsAppOpened(false);
        try {
          await saveDeviceSettings(email, formData.name || knownUser?.name || '', selectedPlayer?.id || '');
        } catch (e) { /* niet fataal */ }
        handleVerificationComplete(data.token, data.user);
      } else {
        setVerifyError(data.message || 'Ongeldige of verlopen code. Controleer de code in je email.');
      }
    } catch (err: any) {
      setVerifyError('Verbindingsfout bij verificatie. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Registratie: "Doorgaan" in register_expand fase ────────────────────────
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isRegisterFormComplete) return;
    setLoading(true);
    setStatusMessage('Profiel zoeken in SBN database...');
    setError('');

    try {
      // Eerst proberen als direct login (e-mail stond al in DB — dubbele check)
      try {
        const directRes = await fetch(`${config.apiUrl}/api/v1/auth/login-direct`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, mobile: fullMobile }),
        });
        if (directRes.ok) {
          const directData = await directRes.json();
          if (directData.success) {
            setStatusMessage('Account gevonden! Welkom terug...');
            handleVerificationComplete(directData.token, directData.user);
            return;
          }
        }
      } catch (directErr) { /* overgeslagen */ }

      // Nieuwe gebruiker: zoek in SBN database op naam
      const requestRes = await fetch(`${config.apiUrl}/api/v1/auth/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          mobile: fullMobile
        }),
      });

      let requestData: any = null;
      try {
        requestData = await requestRes.json();
      } catch (parseErr) {
        setError('Ongeldig serverantwoord. Controleer of de server actief is.');
        return;
      }

      if (requestData?.success) {
        if (requestData.multipleMatches) {
          setMatches(requestData.matches);
          setPhase('player_select');
        } else {
          const player = requestData.player;
          setSelectedPlayer(player);
          await sendOtpCode(formData.email, formData.name, player);
        }
      } else {
        setError(requestData?.message || 'Er is iets misgegaan.');
      }
    } catch (err: any) {
      setError(err?.message || 'Onverwachte fout opgetreden.');
    } finally {
      setLoading(false);
      setStatusMessage('');
    }
  };

  // ─── Passkey registratie ─────────────────────────────────────────────────────
  const handleRegisterPasskey = async () => {
    if (!verifiedToken) return;
    setPasskeyRegistering(true);
    try {
      const deviceSettings = getDeviceSettings();
      const email = verifiedUser?.email || formData.email || emailInput;
      const result = await registerPasskey(email, verifiedToken, deviceSettings.deviceId);
      if (result && result.success) {
        localStorage.setItem('squash_device_settings', JSON.stringify({
          ...deviceSettings,
          email: email.toLowerCase().trim(),
          refereeName: verifiedUser?.name,
          refereePlayerId: verifiedUser?.playerId,
          passkeyCredentialId: result.credentialId,
          passkeyRegisteredAt: new Date().toISOString()
        }));
        localStorage.setItem('remembered_email', email.toLowerCase().trim());
        onLoginSuccess(verifiedUser, verifiedToken);
      } else {
        setError('Passkey registratie mislukt. Probeer het later opnieuw via Instellingen.');
      }
    } catch (err) {
      setError('Onverwachte fout bij passkey registratie.');
    } finally {
      setPasskeyRegistering(false);
    }
  };

  const handleSkipPasskey = () => {
    if (verifiedUser && verifiedToken) {
      onLoginSuccess(verifiedUser, verifiedToken);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="login-container">
      <div className="login-card glass">
        <div className="login-header">
          <h1 className="neon-text">Squash Scorer</h1>
          {phase === 'email_input' && (
            <p className="subtitle" style={{ lineHeight: '1.6', fontSize: '0.9rem' }}>
              Voer je e-mailadres in om door te gaan.
            </p>
          )}
          {phase === 'register_expand' && (
            <p className="subtitle" style={{ lineHeight: '1.6', fontSize: '0.9rem' }}>
              Welkom! Vul je gegevens in om een account aan te maken.
            </p>
          )}
        </div>

        {error && <div className="error-badge">{error}</div>}

        {/* ── FASE 0: E-mail Eerst Poortwachter ─────────────────────────── */}
        {phase === 'email_input' && (
          <div className="login-form gatekeeper-phase">
            <div className="input-field">
              <label>E-mailadres</label>
              <input
                id="gatekeeper-email"
                type="email"
                autoFocus
                autoComplete="email"
                value={emailInput}
                onChange={e => { setEmailInput(e.target.value); setCheckEmailError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCheckEmail(); } }}
                placeholder="naam@voorbeeld.nl"
                style={{ marginBottom: 0 }}
              />
              {checkEmailError && <span className="error-text-small">{checkEmailError}</span>}
            </div>
            <button
              id="gatekeeper-continue-btn"
              type="button"
              className="login-btn primary"
              disabled={checkEmailLoading || emailInput.length < 5}
              onClick={handleCheckEmail}
            >
              {checkEmailLoading ? (
                <div className="loading-spinner-container">
                  <span className="spinner small"></span>
                  <span className="loading-text">Controleren...</span>
                </div>
              ) : 'Doorgaan →'}
            </button>
            {passkeyLoginError && (
              <div style={{ marginTop: '12px' }}>
                <div className="error-text-small center">{passkeyLoginError}</div>
                <button
                  type="button"
                  className="text-btn"
                  style={{ marginTop: '8px', fontSize: '0.85rem' }}
                  onClick={() => sendOtpCode(emailInput.toLowerCase().trim(), knownUser?.name || '', null)}
                >
                  ✉️ Inloggen met code
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── FASE A: Bestaande gebruiker — Passkey Hero ─────────────────── */}
        {phase === 'passkey_prompt' && savedSettings?.email && (
          <div className="login-form passkey-hero-card" style={{ textAlign: 'center' }}>
            <div className="passkey-icon-container" style={{ margin: '0 auto 15px' }}>
              <span className="passkey-main-icon" style={{ fontSize: '2.5rem' }}>🔑</span>
            </div>
            <h2 style={{ fontSize: '1.25rem', marginBottom: '4px', fontWeight: 600 }}>
              Welkom terug{savedSettings.refereeName ? `, ${savedSettings.refereeName}` : ''}!
            </h2>
            <p style={{ opacity: 0.7, fontSize: '0.9rem', marginBottom: '24px' }}>
              {savedSettings.email}
            </p>

            <button
              type="button"
              className="login-btn primary"
              onClick={() => handlePasskeyLogin(savedSettings.email)}
              disabled={loading}
              style={{ width: '100%', padding: '14px', fontSize: '1.05rem', marginBottom: '10px' }}
            >
              {loading ? <span className="spinner small"></span> : '🔑 Inloggen met Passkey'}
            </button>

            {passkeyLoginError && (
              <div style={{ marginBottom: '12px' }}>
                <div className="error-text-small center">{passkeyLoginError}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.65, marginTop: '6px', textAlign: 'center' }}>
                  Passkey niet meer geldig? Gebruik de fallback via je e-mailadres.
                </div>
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
              <button
                type="button"
                className="text-btn"
                onClick={async () => {
                  // Stuur direct een OTP-code, zonder opnieuw via check-email te gaan
                  // (voorkomt deadlock als passkey op het apparaat verdwenen is)
                  const email = (savedSettings.email || '').trim().toLowerCase();
                  if (email) {
                    setForceOtp(true);
                    setEmailInput(email);
                    await sendOtpCode(email, knownUser?.name || savedSettings.refereeName || '', null);
                  } else {
                    setForceOtp(true);
                    setEmailInput('');
                    setPhase('email_input');
                  }
                }}
                style={{ fontSize: '0.9rem' }}
              >
                ✉️ Inloggen via e-mailadres
              </button>
              <button
                type="button"
                className="text-btn"
                style={{ opacity: 0.5, fontSize: '0.8rem' }}
                onClick={() => {
                  setEmailInput('');
                  setPhase('email_input');
                }}
              >
                Nieuw profiel / Ander account
              </button>
            </div>
          </div>
        )}

        {/* ── FASE B: Nieuwe gebruiker — Registratieformulier ─────────────── */}
        {phase === 'register_expand' && (
          <form className="login-form" onSubmit={handleRegisterSubmit}
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}>

            <div className="input-field">
              <label>E-mailadres</label>
              <input
                type="email"
                required
                value={formData.email}
                readOnly
                style={{ opacity: 0.7, cursor: 'default' }}
              />
            </div>

            <div className="input-field">
              <label>Volledige Naam (zoals bij SBN)</label>
              <input
                id="register-name"
                type="text"
                required
                autoFocus
                tabIndex={1}
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Bijv. Jan de Vries"
              />
            </div>

            <div className="input-field">
              <label>Mobiel Nummer (WhatsApp)</label>
              <input
                id="register-mobile"
                type="tel"
                required
                tabIndex={2}
                value={formData.mobile}
                onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="+31 6 12345678"
              />
            </div>

            <div className="login-actions-container">
              <button
                type="submit"
                className="login-btn primary"
                disabled={loading || !isRegisterFormComplete}
              >
                {loading ? (
                  <div className="loading-spinner-container">
                    <span className="spinner"></span>
                    <span className="loading-text">{statusMessage || 'Laden...'}</span>
                  </div>
                ) : 'Account aanmaken'}
              </button>
            </div>

            <button
              type="button"
              className="text-btn"
              style={{ marginTop: '16px', fontSize: '0.85rem' }}
              onClick={() => { setPhase('email_input'); setError(''); }}
            >
              ← Terug
            </button>
          </form>
        )}

        {/* ── Speler selectie (meerdere SBN profielen) ────────────────────── */}
        {phase === 'player_select' && (
          <div className="selection-list">
            <h3>Meerdere spelers gevonden. Kies jouw profiel:</h3>
            <div className="scroll-area">
              {matches.map(m => (
                <div key={m.id} className="player-match"
                  onClick={() => { setSelectedPlayer(m); sendOtpCode(formData.email || emailInput, formData.name, m); }}>
                  <span className="match-name">{m.name}</span>
                  <span className="match-club">{m.club}</span>
                  <span className="match-id">ID: {m.id}</span>
                </div>
              ))}
              <div className="player-match none"
                onClick={() => { setSelectedPlayer(null); sendOtpCode(formData.email || emailInput, formData.name, null); }}>
                Geen van deze (Doorgaan zonder koppeling)
              </div>
            </div>
          </div>
        )}

        {/* ── OTP Verificatie ──────────────────────────────────────────────── */}
        {phase === 'otp_verify' && (
          <div className="login-form">
            <div className="code-instruction">
              We hebben je een 6-cijferige code gestuurd via <strong>e-mail</strong> naar{' '}
              <strong>{formData.email || emailInput}</strong>.<br /><br />
              Voer de code hieronder in om in te loggen, of stuur de code optioneel via WhatsApp.
            </div>

            <div className="auth-channel-box">
              <div className="input-field center">
                <input
                  id="otp-code-input"
                  type="text"
                  maxLength={6}
                  required
                  className="code-input"
                  value={code}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    setCode(val);
                    if (val.length !== 6) setWhatsAppOpened(false);
                    if (val.length === 6) {
                      setVerifyError('');
                      handleVerifyDirect(val);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && code.length === 6) {
                      e.preventDefault();
                      handleVerifyDirect();
                    }
                  }}
                  placeholder="000000"
                  autoFocus
                  disabled={loading}
                />
                {verifyError && <span className="error-text-small">{verifyError}</span>}
              </div>

              <div className="speedrun-section" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="button"
                  className="login-btn primary"
                  disabled={code.length !== 6 || loading}
                  onClick={() => handleVerifyDirect()}
                >
                  {loading ? <><span className="spinner small"></span> Verifiëren...</> : '✅ Inloggen met code'}
                </button>

                {code.length === 6 && (
                  <a
                    href={`https://wa.me/${WHATSAPP_BOT_NUMBER}?text=${encodeURIComponent(code)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`login-btn whatsapp-speedrun ${whatsAppOpened ? 'whatsapp-waiting' : ''}`}
                    onClick={() => setWhatsAppOpened(true)}
                    style={{ opacity: 0.85, fontSize: '0.9rem' }}
                  >
                    {whatsAppOpened
                      ? <><span className="spinner small"></span> Wachten op WhatsApp verificatie...</>
                      : <>📱 Of verifieer via WhatsApp</>}
                  </a>
                )}
              </div>
            </div>

            <button type="button" className="text-btn" style={{ marginTop: '25px' }}
              onClick={() => sendOtpCode(formData.email || emailInput, formData.name || knownUser?.name || '', selectedPlayer)}>
              ↻ Nieuwe code aanvragen
            </button>
            <button type="button" className="text-btn"
              onClick={() => { setPhase('email_input'); setVerifyError(''); setCode(''); setPollingActive(false); }}
              disabled={loading}>
              ← Terug naar invoer
            </button>
          </div>
        )}

        {/* ── Passkey registratie aanbieden (na eerste succesvolle OTP) ────── */}
        {phase === 'passkey_register' && (
          <div className="login-form passkey-step">
            <div className="passkey-icon-container">
              <span className="passkey-main-icon">🔑</span>
            </div>
            <h2 className="passkey-title">Sneller inloggen met passkey</h2>
            <p className="passkey-description">
              Registreer een passkey om voortaan in te loggen met je gezicht, vingerafdruk of PIN.
              Je hebt dan geen e-mail of WhatsApp verificatie meer nodig op dit apparaat.
            </p>

            {passkeySupported ? (
              <div className="passkey-actions">
                <button
                  className="login-btn primary passkey-reg-btn"
                  onClick={handleRegisterPasskey}
                  disabled={passkeyRegistering}
                >
                  {passkeyRegistering ? <span className="spinner"></span> : '🔐 Passkey registreren'}
                </button>
                <button className="text-btn skip-btn" onClick={handleSkipPasskey} disabled={passkeyRegistering}>
                  Nu overslaan
                </button>
              </div>
            ) : (
              <div className="passkey-fallback">
                <p className="info-text-small">
                  Passkeys worden niet ondersteund op dit apparaat of in deze browser.
                </p>
                <button className="login-btn primary" onClick={handleSkipPasskey}>
                  Doorgaan naar dashboard
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Login;
